import { openDB } from 'idb';

const DB_NAME = 'session_logs_db';
const STORE_NAME = 'session_logs';
const ACTIVE_SESSION_STORE = 'active_sessions';
const DB_VERSION = 2;

const isAuthorizedUser = () => {
  const user = JSON.parse(localStorage.getItem('User'));
  return user?.email == process.env.REACT_APP_ADMIN_ACCESS_EMAIL || user?.email == 'techsavvy@gmail.com';
};

const checkAuthorization = () => {
  if (!isAuthorizedUser()) {
    throw new Error('Unauthorized access to session logs database');
  }
};

const createSessionLogsStore = (db) => {
  const store = db.createObjectStore(STORE_NAME, {
    keyPath: 'id',
    autoIncrement: true,
  });
  
  store.createIndex('userId', 'userId');
  store.createIndex('profileName', 'profileName');
  store.createIndex('userName', 'userName');
  store.createIndex('sessionDate', 'sessionDate');
  store.createIndex('profileId', 'profileId');
  store.createIndex('sessionStartTime', 'sessionStartTime');
  
  return store;
};

const createActiveSessionsStore = (db) => {
  if (!db.objectStoreNames.contains(ACTIVE_SESSION_STORE)) {
    const activeStore = db.createObjectStore(ACTIVE_SESSION_STORE, {
      keyPath: 'sessionId'
    });
    activeStore.createIndex('userId', 'userId');
    activeStore.createIndex('lastUpdated', 'lastUpdated');
    return activeStore;
  }
  return null;
};

const verifyStoresExist = (db) => {
  const requiredStores = [STORE_NAME, ACTIVE_SESSION_STORE];
  const missingStores = requiredStores.filter(store => !db.objectStoreNames.contains(store));
  if (missingStores.length > 0) {
    throw new Error(`Missing required stores: ${missingStores.join(', ')}`);
  }
};

export const initSessionLogsDB = async () => {
  try {
    checkAuthorization();
    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        // Handle version upgrades
        switch (oldVersion) {
          case 0: {
            // Create initial session logs store
            createSessionLogsStore(db);
          }
          // Falls through to next case
          case 1: {
            // Add active sessions store in version 2
            createActiveSessionsStore(db);
            break;
          }
          default:
            break;
        }
      },
      blocked() {
        // Called if there are older versions of the database open on the origin
        console.warn('Please close other tabs with this site open');
      },
      blocking() {
        // Called if this version of the database blocks a newer version
        console.warn('Please reload this tab to use the latest version');
      },
      terminated() {
        // Called if the database connection is terminated unexpectedly
        console.error('Database connection terminated unexpectedly');
      }
    });

    // Verify all required stores exist
    verifyStoresExist(db);
    return db;
  } catch (error) {
    if (error.name === 'VersionError' || error.message.includes('Missing required stores')) {
      // If version error or missing stores, try to delete the database and recreate it
      try {
        await deleteDB();
        return initSessionLogsDB();
      } catch (deleteError) {
        console.error('Error recreating database:', deleteError);
        throw deleteError;
      }
    }
    console.error('Error initializing session logs database:', error);
    throw error;
  }
};

// Helper function to delete the database if needed
export const deleteDB = async () => {
  try {
    await window.indexedDB.deleteDatabase(DB_NAME);
    return true;
  } catch (error) {
    console.error('Error deleting database:', error);
    throw error;
  }
};

// Function to check and cleanup database if needed
export const checkAndCleanupDB = async () => {
  try {
    const db = await initSessionLogsDB();
    if (db.version !== DB_VERSION) {
      await deleteDB();
      return initSessionLogsDB();
    }
    // Verify stores exist after version check
    verifyStoresExist(db);
    return db;
  } catch (error) {
    console.error('Error checking database:', error);
    throw error;
  }
};

export const saveSessionLog = async (sessionData) => {
  try {
    checkAuthorization();
    const db = await checkAndCleanupDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    // Add sessionDate for easy filtering
    const sessionDate = new Date(sessionData.sessionStartTime).toISOString().split('T')[0];
    
    await store.add({
      ...sessionData,
      sessionDate,
      timestamp: new Date().toISOString()
    });
    
    await tx.done;
  } catch (error) {
    console.error('Error saving session log:', error);
    throw error;
  }
};

export const getSessionLogs = async ({
  page = 1,
  limit = 10,
  userId = null,
  profileName = null,
  userName = null,
  dateFrom = null,
  dateTo = null,
  profileId = null
} = {}) => {
  try {
    checkAuthorization();
    const db = await checkAndCleanupDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    let logs = [];
    
    // Apply filters
    if (userId) {
      logs = await store.index('userId').getAll(userId);
    } else if (profileName) {
      logs = await store.index('profileName').getAll(profileName);
    } else if (userName) {
      logs = await store.index('userName').getAll(userName);
    } else if (profileId) {
      logs = await store.index('profileId').getAll(profileId);
    } else {
      logs = await store.getAll();
    }
    
    // Apply date filter
    if (dateFrom || dateTo) {
      logs = logs.filter(log => {
        const logDate = new Date(log.sessionStartTime);
        if (dateFrom && dateTo) {
          return logDate >= new Date(dateFrom) && logDate <= new Date(dateTo);
        } else if (dateFrom) {
          return logDate >= new Date(dateFrom);
        } else if (dateTo) {
          return logDate <= new Date(dateTo);
        }
        return true;
      });
    }
    
    // Sort by sessionStartTime in descending order
    logs.sort((a, b) => new Date(b.sessionStartTime) - new Date(a.sessionStartTime));
    
    // Calculate pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedLogs = logs.slice(startIndex, endIndex);
    
    const total = logs.length;
    const totalPages = Math.ceil(total / limit);
    
    await tx.done;
    
    return {
      logs: paginatedLogs,
      pagination: {
        total,
        totalPages,
        currentPage: page,
        limit
      }
    };
  } catch (error) {
    console.error('Error getting session logs:', error);
    throw error;
  }
};

export const getSessionStats = async (dateFrom = null, dateTo = null) => {
  try {
    checkAuthorization();
    const db = await checkAndCleanupDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    let logs = await store.getAll();
    
    // Apply date filter
    if (dateFrom || dateTo) {
      logs = logs.filter(log => {
        const logDate = new Date(log.sessionStartTime);
        if (dateFrom && dateTo) {
          return logDate >= new Date(dateFrom) && logDate <= new Date(dateTo);
        } else if (dateFrom) {
          return logDate >= new Date(dateFrom);
        } else if (dateTo) {
          return logDate <= new Date(dateTo);
        }
        return true;
      });
    }
    
    // Calculate statistics
    const stats = {
      totalSessions: logs.length,
      totalActiveTime: logs.reduce((sum, log) => sum + log.totalActiveTime, 0),
      averageSessionDuration: logs.reduce((sum, log) => sum + log.sessionDuration, 0) / logs.length,
      userStats: {},
      profileStats: {},
      dailyStats: {}
    };
    
    // Calculate user and profile stats
    logs.forEach(log => {
      // User stats
      if (!stats.userStats[log.userName]) {
        stats.userStats[log.userName] = {
          totalSessions: 0,
          totalActiveTime: 0
        };
      }
      stats.userStats[log.userName].totalSessions++;
      stats.userStats[log.userName].totalActiveTime += log.totalActiveTime;
      
      // Profile stats
      if (!stats.profileStats[log.profileName]) {
        stats.profileStats[log.profileName] = {
          totalSessions: 0,
          totalActiveTime: 0
        };
      }
      stats.profileStats[log.profileName].totalSessions++;
      stats.profileStats[log.profileName].totalActiveTime += log.totalActiveTime;
      
      // Daily stats
      const date = log.sessionDate;
      if (!stats.dailyStats[date]) {
        stats.dailyStats[date] = {
          totalSessions: 0,
          totalActiveTime: 0
        };
      }
      stats.dailyStats[date].totalSessions++;
      stats.dailyStats[date].totalActiveTime += log.totalActiveTime;
    });
    
    await tx.done;
    return stats;
  } catch (error) {
    console.error('Error getting session stats:', error);
    throw error;
  }
};

export const saveActiveSession = async (sessionData) => {
  try {
    const db = await checkAndCleanupDB();
    const tx = db.transaction(ACTIVE_SESSION_STORE, 'readwrite');
    const store = tx.objectStore(ACTIVE_SESSION_STORE);
    
    await store.put({
      ...sessionData,
      lastUpdated: new Date().toISOString()
    });
    
    await tx.done;
  } catch (error) {
    console.error('Error saving active session:', error);
  }
};

export const getActiveSession = async (sessionId) => {
  try {
    const db = await initSessionLogsDB();
    const tx = db.transaction(ACTIVE_SESSION_STORE, 'readonly');
    const store = tx.objectStore(ACTIVE_SESSION_STORE);
    
    const session = await store.get(sessionId);
    await tx.done;
    return session;
  } catch (error) {
    console.error('Error getting active session:', error);
    return null;
  }
};

export const removeActiveSession = async (sessionId) => {
  try {
    const db = await initSessionLogsDB();
    const tx = db.transaction(ACTIVE_SESSION_STORE, 'readwrite');
    const store = tx.objectStore(ACTIVE_SESSION_STORE);
    
    await store.delete(sessionId);
    await tx.done;
  } catch (error) {
    console.error('Error removing active session:', error);
  }
};

export const cleanupStaleSessions = async (olderThanMinutes = 30) => {
  try {
    const db = await initSessionLogsDB();
    const tx = db.transaction(ACTIVE_SESSION_STORE, 'readwrite');
    const store = tx.objectStore(ACTIVE_SESSION_STORE);
    
    const cutoffTime = new Date();
    cutoffTime.setMinutes(cutoffTime.getMinutes() - olderThanMinutes);
    
    const allSessions = await store.getAll();
    
    for (const session of allSessions) {
      if (new Date(session.lastUpdated) < cutoffTime) {
        // Save the stale session as completed
        await saveSessionLog({
          ...session,
          endReason: 'session_timeout',
          sessionEndTime: session.lastUpdated
        });
        // Remove from active sessions
        await store.delete(session.sessionId);
      }
    }
    
    await tx.done;
  } catch (error) {
    console.error('Error cleaning up stale sessions:', error);
  }
};

export const getPageWiseStats = async (searchQuery = '') => {
  try {
    checkAuthorization();
    const db = await checkAndCleanupDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    let logs = await store.getAll();
    
    // Apply search filter if provided
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      logs = logs.filter(log => 
        log.reportPage?.toLowerCase().includes(searchLower) ||
        log.userName?.toLowerCase().includes(searchLower) ||
        log.profileName?.toLowerCase().includes(searchLower) ||
        log.userId?.toLowerCase().includes(searchLower)
      );
    }
    
    // Group by page and calculate stats
    const pageStats = logs.reduce((acc, log) => {
      const page = log.reportPage || 'Unknown';
      
      if (!acc[page]) {
        acc[page] = {
          totalSessions: 0,
          totalActiveTime: 0,
          totalDuration: 0,
          users: new Set(),
          profiles: new Set(),
          lastAccessed: null,
          path: log.path
        };
      }
      
      acc[page].totalSessions++;
      acc[page].totalActiveTime += log.totalActiveTime || 0;
      acc[page].totalDuration += log.sessionDuration || 0;
      acc[page].users.add(log.userName);
      acc[page].profiles.add(log.profileName);
      
      const sessionTime = new Date(log.sessionStartTime);
      if (!acc[page].lastAccessed || sessionTime > new Date(acc[page].lastAccessed)) {
        acc[page].lastAccessed = log.sessionStartTime;
        acc[page].path = log.path;
      }
      
      return acc;
    }, {});
    
    // Convert Sets to arrays and sort by total active time
    const result = Object.entries(pageStats)
      .map(([page, stats]) => ({
        page,
        path: stats.path || '-',
        totalSessions: stats.totalSessions,
        totalActiveTime: stats.totalActiveTime,
        totalDuration: stats.totalDuration,
        uniqueUsers: Array.from(stats.users).length,
        uniqueProfiles: Array.from(stats.profiles).length,
        lastAccessed: stats.lastAccessed
      }))
      .sort((a, b) => b.totalActiveTime - a.totalActiveTime);
    
    await tx.done;
    return result;
  } catch (error) {
    console.error('Error getting page-wise stats:', error);
    throw error;
  }
}; 